import { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useRestaurantId } from "@/hooks/useRestaurantId";
import { queryKeys } from "@/rc/keys";
import { errMessage } from "@/rc/helpers";

const SWEDBANK_MODES = ["complete", "cancel"] as const;
type SwedbankMode = (typeof SWEDBANK_MODES)[number];

const isSwedbankMode = (value: string | null): value is SwedbankMode => {
  return (
    value !== null && (SWEDBANK_MODES as readonly string[]).includes(value)
  );
};

interface SyncStatusResponse {
  error?: string;
  orderPaid?: boolean;
  orderNumber?: number | null;
}

/* -------------------------------------------------------------------------- */
/*  Constants — single source of truth for wire + storage contracts           */
/* -------------------------------------------------------------------------- */

const QP_MODE = "swedbankPay";
const QP_ORDER_ID = "orderId";

const EDGE_FN_SYNC_STATUS = "swedbank-pay-sync-status";
const SS_TERMINAL_PENDING = "comminova_terminal_swedbank_pending";

const ssPaymentOrderKey = (orderId: string) => {
  return `comminova_swedbank_pay_po_${orderId}` as const;
};

/* -------------------------------------------------------------------------- */
/*  Safe session storage — tolerates private mode, disabled storage, SSR.     */
/* -------------------------------------------------------------------------- */

const safeSession = {
  get(key: string): string | null {
    if (typeof window === "undefined") return null;
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  remove(key: string): void {
    if (typeof window === "undefined") return;
    try {
      window.sessionStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  },
} as const;

function clearSwedbankSession(orderId: string): void {
  safeSession.remove(SS_TERMINAL_PENDING);
  safeSession.remove(ssPaymentOrderKey(orderId));
}

/* -------------------------------------------------------------------------- */
/*  Async worker — pure function, unit-testable in isolation.                 */
/* -------------------------------------------------------------------------- */

interface SyncCompleteArgs {
  orderId: string;
  restaurantId: string | null | undefined;
  signal: AbortSignal;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * After a Swedbank redirect checkout the payer returns to the same pathname
 * with `?swedbankPay=complete|cancel&orderId=...`. This component:
 *
 *   1. Reads those params once per unique `(mode, orderId)` pair.
 *   2. On `complete`: calls the `swedbank-pay-sync-status` Edge function
 *      (with staff JWT), toasts the outcome, and invalidates the admin
 *      orders query on success.
 *   3. On `cancel`: toasts and clears the terminal pending flag.
 *   4. Always strips the query params and clears related session keys.
 *
 * Renders nothing. Mount once in the staff/terminal layout.
 */
export function SwedbankStaffReturnSync(): null {
  const { t } = useTranslation("terminal");
  const queryClient = useQueryClient();
  const restaurantId = useRestaurantId();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawMode = searchParams.get(QP_MODE);
  const orderId = searchParams.get(QP_ORDER_ID);
  const mode: SwedbankMode | null = isSwedbankMode(rawMode) ? rawMode : null;

  // Guards against Strict-Mode double-invocation and duplicate runs
  // triggered by unrelated re-renders.
  const processedRef = useRef<string | null>(null);

  const stripQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(QP_MODE);
        next.delete(QP_ORDER_ID);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const syncSwedbankComplete = useCallback(
    async ({ orderId, restaurantId, signal }: SyncCompleteArgs) => {
      const paymentOrderId =
        safeSession.get(ssPaymentOrderKey(orderId)) ?? undefined;

      try {
        const { data, error } =
          await supabase.functions.invoke<SyncStatusResponse>(
            EDGE_FN_SYNC_STATUS,
            {
              body: {
                orderId,
                ...(paymentOrderId ? { paymentOrderId } : {}),
              },
            }
          );

        if (signal.aborted) return;
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        if (data?.orderPaid) {
          toast.success(t("swedbank.syncPaidTitle"), {
            description: t("swedbank.syncPaidDesc", {
              number: data.orderNumber != null ? String(data.orderNumber) : "—",
            }),
          });
          if (restaurantId) {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.orders.adminList(restaurantId),
            });
          }
        } else {
          toast.info(t("swedbank.syncPendingTitle"), {
            description: t("swedbank.syncPendingDesc"),
          });
        }
      } catch (e) {
        if (signal.aborted) return;
        toast.error(t("swedbank.syncErrorTitle"), {
          description: errMessage(e) || t("swedbank.syncErrorDesc"),
        });
      } finally {
        clearSwedbankSession(orderId);
        if (!signal.aborted) {
          stripQuery();
        }
      }
    },
    [queryClient, stripQuery, t]
  );

  const handleSwedbankCancelReturn = useCallback(
    (cancelOrderId: string) => {
      toast.success(t("swedbank.cancelReturnTitle"), {
        description: t("swedbank.cancelReturnDesc"),
      });
      // Still sync with Swedbank so the DB status moves from "pending" to
      // "failed" (Swedbank will report Cancelled/Aborted). Fire-and-forget.
      const paymentOrderId =
        safeSession.get(ssPaymentOrderKey(cancelOrderId)) ?? undefined;
      void supabase.functions
        .invoke(EDGE_FN_SYNC_STATUS, {
          body: {
            orderId: cancelOrderId,
            ...(paymentOrderId ? { paymentOrderId } : {}),
          },
        })
        .catch(() => {
          /* best-effort */
        });
      clearSwedbankSession(cancelOrderId);
      stripQuery();
    },
    [stripQuery, t]
  );

  // Reset the dedupe key when the relevant params disappear, so that a
  // future redirect with the same orderId is processed fresh.
  useEffect(() => {
    if (!mode || !orderId) {
      processedRef.current = null;
    }
  }, [mode, orderId]);

  useEffect(() => {
    if (!mode || !orderId) {
      return;
    }

    const key = `${mode}:${orderId}`;
    if (processedRef.current === key) {
      return;
    }

    processedRef.current = key;

    if (mode === "cancel") {
      handleSwedbankCancelReturn(orderId);
      return;
    }

    // mode === "complete" — exhaustive over SwedbankMode
    const controller = new AbortController();
    syncSwedbankComplete({
      orderId,
      restaurantId,
      signal: controller.signal,
    });

    return () => {
      controller.abort();
    };
  }, [
    mode,
    orderId,
    restaurantId,
    syncSwedbankComplete,
    handleSwedbankCancelReturn,
  ]);

  return null;
}
